<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import PrioritySelector from '../components/PrioritySelector.vue';
import AssigneeSelector from '../components/AssigneeSelector.vue';
import TeamSelector from '../components/TeamSelector.vue';
import ContactSelector from '../components/ContactSelector.vue';
import SlaPolicySelector from '../components/SlaPolicySelector.vue';
import BoardSelector from '../components/BoardSelector.vue';
import BoardColumnSelector from '../components/BoardColumnSelector.vue';
import ServiceSelector from '../components/ServiceSelector.vue';
import SubServiceSelector from '../components/SubServiceSelector.vue';
import SlaComponent from '../components/SlaComponent.vue';
import BusinessAccountSelector from '../components/BusinessAccountSelector.vue';
import UpdatedSlaEventComponent from '../components/UpdatedSlaEventComponent.vue';
import DeletedSlaEventComponent from '../components/DeletedSlaEventComponent.vue';
import AddCommentForm from '../components/AddCommentForm.vue';
import TicketActivityContentText from '../components/TicketActivityContentText.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { format } from 'date-fns';
import { formatHistoryValue as formatHistoryValueFromHelper } from 'dashboard/helper/ticketHistoryHelper';
import TicketCustomAttributesDisplay from 'dashboard/components-next/Tickets/TicketCustomAttributesDisplay.vue';
import TicketEmailComposer from '../components/TicketEmailComposer.vue';
import TicketAttachments from '../components/TicketAttachments.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  ticketId: {
    type: [Number, String],
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
  columnId: {
    type: [Number, String],
    default: null,
  },
  hasNext: {
    type: Boolean,
    default: false,
  },
  hasPrevious: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'navigateNext', 'navigatePrevious']);
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const error = ref(null);
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getMyTeams');
const contacts = useMapGetter('contacts/getContacts');
const ticket = useMapGetter('tickets/getCurrentTicket');
const statuses = useMapGetter('tickets/getStatuses');
const priorities = useMapGetter('tickets/getPriorities');
const slaPolicies = useMapGetter('tickets/getSlaPolicies');
const services = useMapGetter('services/getServices');
const boards = computed(() => store.getters['boards/getBoards'] || []);
const businessAccounts = computed(
  () => store.getters['businessAccounts/getBusinessAccountsList'] || []
);
const MAX_DESCRIPTION_LENGTH = 1000;

const listTickets = computed(() => store.getters['tickets/getTickets'] || []);
const activeTicketId = computed(() => {
  const rawId = props.ticketId || route.params.id;
  const parsed = Number(rawId);
  return Number.isFinite(parsed) ? parsed : null;
});

const listTicketIndex = computed(() => {
  if (!activeTicketId.value) return -1;
  return listTickets.value.findIndex(
    item => Number(item.id) === activeTicketId.value
  );
});

const listNavigationAvailable = computed(() => {
  return listTicketIndex.value >= 0 && listTickets.value.length > 1;
});

const shouldShowNavigationArrows = computed(() => {
  // Kanban modal navigation (columnId present) OR list view navigation (tickets loaded in store)
  return (
    Boolean(props.columnId) || (!props.isModal && listNavigationAvailable.value)
  );
});

const effectiveHasNext = computed(() => {
  if (props.columnId) return props.hasNext;
  return (
    listTicketIndex.value >= 0 &&
    listTicketIndex.value < listTickets.value.length - 1
  );
});

const effectiveHasPrevious = computed(() => {
  if (props.columnId) return props.hasPrevious;
  return listTicketIndex.value > 0;
});

const boardColumns = computed(() => {
  if (!ticket.value?.board_id) return [];
  const board = boards.value.find(b => b.id === ticket.value.board_id);
  return board?.board_columns || [];
});

// Only show teams that have a board (or the current ticket's team) so selector never offers teams without board/column
const teamsForSelector = computed(() => {
  const list = teams.value || [];
  const currentTeamId = ticket.value?.team_id;
  return list.filter(
    t =>
      (boards.value || []).some(b => b.team_id === t.id) ||
      (currentTeamId != null && t.id === currentTeamId)
  );
});

const upsertBoard = board => {
  if (!board) return;
  const currentBoards = boards.value || [];
  const boardIndex = currentBoards.findIndex(b => b.id === board.id);

  if (boardIndex === -1) {
    store.commit('boards/SET_BOARDS', [...currentBoards, board]);
  } else {
    const updatedBoards = [...currentBoards];
    updatedBoards[boardIndex] = board;
    store.commit('boards/SET_BOARDS', updatedBoards);
  }
};

const getFirstColumnId = board => {
  if (!board?.board_columns?.length) return null;
  const sortedColumns = [...board.board_columns].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  );
  return sortedColumns[0]?.id || null;
};

const fetchTicket = async ticketId => {
  try {
    error.value = null;
    await store.dispatch('tickets/getTicket', ticketId);
  } catch (err) {
    error.value = err.message || 'Erro ao carregar ticket';
  }
};

const fetchTicketHistory = async ticketId => {
  try {
    error.value = null;
    await store.dispatch('tickets/getTicketHistory', ticketId);
  } catch (err) {
    error.value = err.message || 'Erro ao carregar histórico do ticket';
  }
};

const updateTicketBoard = async (boardId, boardColumnId) => {
  if (!ticket.value?.id) return;

  const normalizedBoardId = boardId ?? null;
  const normalizedColumnId = boardColumnId ?? null;

  if (
    ticket.value.board_id === normalizedBoardId &&
    ticket.value.board_column_id === normalizedColumnId
  ) {
    return;
  }

  try {
    await store.dispatch('tickets/changeBoard', {
      id: ticket.value.id,
      boardId: normalizedBoardId,
      boardColumnId: normalizedColumnId,
    });

    // Reload ticket and history to ensure status label is updated
    await fetchTicket(ticket.value.id);
    await fetchTicketHistory(ticket.value.id);
  } catch (err) {
    // Error updating board automatically
  }
};

const syncBoardWithTeam = async teamId => {
  if (!ticket.value?.id) return;

  if (!teamId) {
    await updateTicketBoard(null, null);
    return;
  }

  try {
    await store.dispatch('boards/getByTeam', teamId);
    const teamBoard = store.getters['boards/getBoard'];

    if (!teamBoard) {
      await updateTicketBoard(null, null);
      return;
    }

    upsertBoard(teamBoard);
    const firstColumnId = getFirstColumnId(teamBoard);
    await updateTicketBoard(teamBoard.id, firstColumnId);
  } catch (err) {
    // Error syncing board with team
  }
};

watch(
  () => ticket.value?.team_id,
  async (newTeamId, oldTeamId) => {
    if (typeof oldTeamId === 'undefined' || newTeamId === oldTeamId) return;
    await syncBoardWithTeam(newTeamId);
  }
);

const loading = computed(
  () => store.getters['tickets/getUIFlags'].isFetchingTicket
);
const loadingHistory = computed(
  () => store.getters['tickets/getUIFlags'].isFetchingHistory
);

// Function to translate boolean values in history content
const translateBooleanValues = content => {
  if (!content || typeof content !== 'string') return content;

  const yesValue = t('TICKETS.SLA.YES').toLowerCase();
  const noValue = t('TICKETS.SLA.NO').toLowerCase();

  return content
    .replace(/: true\b/gi, `: ${yesValue}`)
    .replace(/: false\b/gi, `: ${noValue}`);
};

const formatHistoryValue = value =>
  formatHistoryValueFromHelper(value, {
    yesLabel: t('TICKETS.SLA.YES'),
    noLabel: t('TICKETS.SLA.NO'),
  });

// Use store data directly instead of local ref
const activities = computed(() => {
  const history = store.getters['tickets/getTicketHistory'] || [];

  // Process all history entries uniformly
  const processedActivities = history.map(historyItem => {
    const systemAction = historyItem.system_action === true;
    const userDisplay = systemAction ? null : historyItem.user_name;
    return {
      id: `h_${historyItem.id}`,
      user: userDisplay,
      action: historyItem.action_name,
      content: translateBooleanValues(historyItem.new_value),
      oldValue: historyItem.old_value,
      newValue: historyItem.new_value,
      slaChanges: historyItem.sla_changes,
      timestamp: historyItem.created_at,
      avatar: userDisplay ? userDisplay.charAt(0).toUpperCase() : null,
      attachments: historyItem.metadata?.attachments || [],
      systemAction,
    };
  });

  // Sort by timestamp (newest first)
  processedActivities.sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );
  return processedActivities;
});
const loadingPriority = ref(false);
const loadingAssignee = ref(false);
const loadingContact = ref(false);

// Check if ticket has email conversation
const hasEmailConversation = ref(false);
const isLoadingEmailCheck = ref(false);

const checkEmailConversation = async () => {
  if (!ticket.value?.conversation_id) {
    hasEmailConversation.value = false;
    return;
  }
  // Conversations API expects display_id in the URL, not the internal conversation id
  const displayId = ticket.value.conversation_display_id;
  if (displayId == null) {
    hasEmailConversation.value = false;
    return;
  }

  isLoadingEmailCheck.value = true;
  try {
    const response = await ConversationApi.show(displayId);
    const conversation = response.data;
    const channel = conversation?.meta?.channel;
    const isEmail = channel === INBOX_TYPES.EMAIL;
    hasEmailConversation.value = isEmail;
  } catch (err) {
    hasEmailConversation.value = false;
  } finally {
    isLoadingEmailCheck.value = false;
  }
};

// Watch ticket changes to check conversation type
watch(
  () => ticket.value?.conversation_id,
  async newConversationId => {
    if (newConversationId) {
      await checkEmailConversation();
    } else if (ticket.value != null) {
      // Only clear when we have a ticket without conversation_id; skip when ticket not loaded yet (first run with immediate)
      hasEmailConversation.value = false;
    }
  },
  { immediate: true }
);
const loadingResolve = ref(false);
const loadingCancel = ref(false);
const loadingTitle = ref(false);
const loadingDescription = ref(false);
const isEditingTitle = ref(false);
const isEditingDescription = ref(false);
const editedTitle = ref('');
const editedDescription = ref('');
const updateHistory = ref(true);
const editable = ref(true);
const showIcon = ref(true);
const serviceError = ref(null);
const updatingService = ref(false);
const serviceForm = ref({
  service: null,
  subService: null,
});
const previousServiceForm = ref({
  service: null,
  subService: null,
});
const isInitialLoad = ref(true);
const showEmailComposer = ref(false);
const isRefreshingTicket = ref(false);

const formatDate = dateString => {
  if (!dateString) return t('TICKETS.SLA.DATE_NOT_AVAILABLE');
  const date = new Date(dateString);
  return format(date, 'HH:mm:ss, dd/MM/yyyy');
};

// SLA Status helper methods
const getSlaStatusText = status => {
  const statusMap = {
    active: 'Ativo',
    hit: 'Cumprido',
    missed: 'Perdido',
    active_with_misses: 'Ativo com Perdas',
  };
  return statusMap[status] || status;
};

const getSlaStatusClass = status => {
  const classMap = {
    active: 'bg-blue-100 text-blue-800',
    hit: 'bg-green-100 text-green-800',
    missed: 'bg-red-100 text-red-800',
    active_with_misses: 'bg-yellow-100 text-yellow-800',
  };
  return classMap[status] || 'bg-gray-100 text-gray-800';
};

const fetchBusinessAccounts = async () => {
  await store.dispatch('businessAccounts/get');
};

const handleCommentAdded = async () => {
  // Refresh ticket and history after comment is added
  // This ensures the status label is updated if status changed automatically
  if (ticket.value?.id) {
    await fetchTicket(ticket.value.id);
    await fetchTicketHistory(ticket.value.id);
  }
};

const toggleResolveStatus = async () => {
  const isResolved = ticket.value?.status_name === 'Resolvido';
  const targetStatus = isResolved ? 'Aberto' : 'Resolvido';
  const newStatus = statuses.value?.find(s => s.name === targetStatus);

  if (!newStatus) {
    error.value = `Status '${targetStatus}' não encontrado`;
    return;
  }

  try {
    loadingResolve.value = true;
    error.value = null;

    await store.dispatch('tickets/changeStatus', {
      id: ticket.value.id,
      newStatus: newStatus.id,
    });

    // Reload ticket to ensure all data is up to date (including status_colors)
    await fetchTicket(ticket.value.id);
    await store.dispatch('tickets/getTicketHistory', ticket.value.id);
  } catch (err) {
    // Error updating status - silently fail
    error.value = err.message || 'Erro ao alterar status do ticket';
  } finally {
    loadingResolve.value = false;
  }
};

const cancelTicket = async () => {
  const cancelledStatus = statuses.value?.find(s => s.name === 'Cancelado');

  if (!cancelledStatus) {
    error.value = 'Status "Cancelado" não encontrado';
    return;
  }

  try {
    loadingCancel.value = true;
    error.value = null;

    await store.dispatch('tickets/changeStatus', {
      id: ticket.value.id,
      newStatus: cancelledStatus.id,
    });

    // Reload ticket to ensure all data is up to date (including status_colors)
    await fetchTicket(ticket.value.id);
    await store.dispatch('tickets/getTicketHistory', ticket.value.id);
  } catch (err) {
    // Error cancelling ticket - silently fail
    error.value = err.message || 'Erro ao cancelar ticket';
  } finally {
    loadingCancel.value = false;
  }
};

const titleInput = ref(null);

const startEditingTitle = async () => {
  editedTitle.value = ticket.value?.title || '';
  isEditingTitle.value = true;
  await nextTick();
  titleInput.value?.focus();
};

const cancelEditingTitle = () => {
  isEditingTitle.value = false;
  editedTitle.value = '';
};

const saveTitle = async () => {
  if (!editedTitle.value.trim()) {
    error.value = 'Título não pode estar vazio';
    return;
  }

  try {
    loadingTitle.value = true;
    error.value = null;

    await store.dispatch('tickets/changeTitle', {
      id: ticket.value.id,
      newTitle: editedTitle.value.trim(),
    });

    await store.dispatch('tickets/getTicketHistory', ticket.value.id);
    isEditingTitle.value = false;
    editedTitle.value = '';
  } catch (err) {
    error.value = err.message || 'Erro ao atualizar título';
  } finally {
    loadingTitle.value = false;
  }
};

const startEditingDescription = () => {
  editedDescription.value = ticket.value?.description || '';
  isEditingDescription.value = true;
};

const cancelEditingDescription = () => {
  isEditingDescription.value = false;
  editedDescription.value = '';
};

const saveDescription = async () => {
  if (!editedDescription.value.trim()) {
    error.value = 'Descrição não pode estar vazia';
    return;
  }
  if (editedDescription.value.length > MAX_DESCRIPTION_LENGTH) {
    error.value = 'Descrição não pode ter mais de 1000 caracteres';
    return;
  }

  try {
    loadingDescription.value = true;
    error.value = null;

    await store.dispatch('tickets/changeDescription', {
      id: ticket.value.id,
      newDescription: editedDescription.value.trim(),
    });

    await store.dispatch('tickets/getTicketHistory', ticket.value.id);
    isEditingDescription.value = false;
    editedDescription.value = '';
  } catch (err) {
    error.value = err.message || 'Erro ao atualizar descrição';
  } finally {
    loadingDescription.value = false;
  }
};

const goBack = () => {
  if (props.isModal) {
    emit('close');
  } else {
    router.push({ name: 'helpdesk_index' });
  }
};

const handleNavigatePrevious = () => {
  if (props.columnId) {
    emit('navigatePrevious');
    return;
  }

  if (!effectiveHasPrevious.value) return;
  const prevTicket = listTickets.value[listTicketIndex.value - 1];
  if (!prevTicket?.id) return;
  router.push({ name: 'helpdesk_ticket_view', params: { id: prevTicket.id } });
};

const handleNavigateNext = () => {
  if (props.columnId) {
    emit('navigateNext');
    return;
  }

  if (!effectiveHasNext.value) return;
  const nextTicket = listTickets.value[listTicketIndex.value + 1];
  if (!nextTicket?.id) return;
  router.push({ name: 'helpdesk_ticket_view', params: { id: nextTicket.id } });
};

const availableSubServices = computed(() => {
  const serviceId = serviceForm.value.service;
  const service = (services.value || []).find(svc => svc.id === serviceId);
  return service?.sub_services || [];
});

const subServices = computed(() => availableSubServices.value);

const hasServices = computed(() => (services.value || []).length > 0);

watch(
  ticket,
  newTicket => {
    const newServiceId = newTicket?.service_id || null;
    const newSubServiceId = newTicket?.sub_service_id || null;

    previousServiceForm.value.service = newServiceId;
    previousServiceForm.value.subService = newSubServiceId;
    serviceForm.value.service = newServiceId;
    serviceForm.value.subService = newSubServiceId;

    // Reset initial load flag after first load
    if (isInitialLoad.value) {
      isInitialLoad.value = false;
    }
  },
  { immediate: true }
);

watch(
  () => serviceForm.value.service,
  (newServiceId, oldServiceId) => {
    const exists = availableSubServices.value.some(
      sub => sub.id === serviceForm.value.subService
    );
    if (!exists) {
      serviceForm.value.subService = null;
    }

    // Auto-save if service changed and not initial load
    // Use nextTick to ensure subservice watch has a chance to update first
    if (
      !isInitialLoad.value &&
      newServiceId !== oldServiceId &&
      newServiceId !== previousServiceForm.value.service
    ) {
      nextTick(() => {
        // Only save if not already saving and service actually changed
        if (
          !updatingService.value &&
          newServiceId !== previousServiceForm.value.service
        ) {
          saveService();
        }
      });
    }
  }
);

watch(
  () => serviceForm.value.subService,
  (newSubServiceId, oldSubServiceId) => {
    // Auto-save if subservice changed and not initial load
    // Only save if service didn't change (to avoid double save when service changes)
    const serviceChanged =
      serviceForm.value.service !== previousServiceForm.value.service;

    if (
      !isInitialLoad.value &&
      !serviceChanged &&
      newSubServiceId !== oldSubServiceId &&
      newSubServiceId !== previousServiceForm.value.subService
    ) {
      if (!updatingService.value) {
        saveService();
      }
    }
  }
);

// Watch for ticketId prop changes (e.g., when navigating in Kanban modal)
watch(
  () => props.ticketId,
  async newTicketId => {
    if (newTicketId) {
      await fetchTicket(newTicketId);
      await fetchTicketHistory(newTicketId);
    }
  }
);

// Watch for route param changes (list navigation updates route params, not props)
watch(
  () => route.params.id,
  async newRouteId => {
    if (props.ticketId) return;
    if (!newRouteId) return;
    await fetchTicket(newRouteId);
    await fetchTicketHistory(newRouteId);
  }
);

// Watch for ticket status changes to ensure label updates reactively
// This is especially important in dev environment where reactivity might not work as expected
watch(
  () => ticket.value?.status_id,
  async (newStatusId, oldStatusId) => {
    // Only refresh if status actually changed, we have a valid ticket, and we're not already refreshing
    if (
      newStatusId &&
      oldStatusId &&
      newStatusId !== oldStatusId &&
      ticket.value?.id &&
      !isRefreshingTicket.value
    ) {
      isRefreshingTicket.value = true;
      try {
        // Small delay to ensure backend has processed the change
        await nextTick();
        // Force a refresh to ensure the label updates with status_colors
        await fetchTicket(ticket.value.id);
      } finally {
        isRefreshingTicket.value = false;
      }
    }
  }
);

const saveService = async () => {
  if (!ticket.value?.id) return;
  serviceError.value = null;

  // Check if there are actual changes
  const serviceChanged =
    serviceForm.value.service !== previousServiceForm.value.service;
  const subServiceChanged =
    serviceForm.value.subService !== previousServiceForm.value.subService;

  if (!serviceChanged && !subServiceChanged) {
    return; // No changes to save
  }

  if (hasServices.value && !serviceForm.value.service) {
    serviceError.value = 'Selecione um serviço';
    return;
  }

  updatingService.value = true;
  try {
    await store.dispatch('tickets/changeService', {
      id: ticket.value.id,
      serviceId: serviceForm.value.service,
      subServiceId: serviceForm.value.subService,
    });

    // Update previous values after successful save
    previousServiceForm.value.service = serviceForm.value.service;
    previousServiceForm.value.subService = serviceForm.value.subService;

    await fetchTicket(ticket.value.id);
    await fetchTicketHistory(ticket.value.id);
  } catch (err) {
    serviceError.value =
      err?.response?.data?.error || err.message || 'Erro ao atualizar serviço';

    // Revert form values on error
    serviceForm.value.service = previousServiceForm.value.service;
    serviceForm.value.subService = previousServiceForm.value.subService;
  } finally {
    updatingService.value = false;
  }
};

const handleEmailSent = async () => {
  // Refresh ticket data to get updated conversation_id if a new conversation was created
  if (ticket.value?.id) {
    await fetchTicket(ticket.value.id);
    await fetchTicketHistory(ticket.value.id);
  }
};

onMounted(async () => {
  const ticketId = props.ticketId || route.params.id;

  if (ticketId) {
    // Fetch ticket data and history
    await fetchTicket(ticketId);
    await fetchTicketHistory(ticketId);

    // Fetch statuses and priorities if not already loaded
    try {
      await store.dispatch('tickets/getStatuses');
      await store.dispatch('tickets/getPriorities');
    } catch (err) {
      // Error fetching statuses/priorities - silently fail
    }

    // Fetch agents and teams for the dropdowns in parallel
    await Promise.allSettled([
      store.dispatch('agents/get'),
      store.dispatch('teams/get'),
      store.dispatch('contacts/get'),
      store.dispatch('tickets/getSlaPolicies'),
      store.dispatch('services/get'),
      store.dispatch('boards/getAll'),
      store.dispatch('attributes/get'),
      fetchBusinessAccounts(),
    ]);
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header -->
    <div class="border-b border-n-weak p-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <button
            v-if="!isModal"
            class="px-3 py-2 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-solid-4 rounded-lg transition-colors"
            @click="goBack"
          >
            {{ t('TICKETS.BACK_ARROW') }}{{ t('TICKETS.BACK') }}
          </button>
          <div class="flex-1">
            <div v-if="!isEditingTitle" class="flex items-center gap-2">
              <!-- Navigation arrows - Kanban (columnId) or Tickets list (store tickets) -->
              <div
                v-if="shouldShowNavigationArrows"
                class="flex items-center gap-1 mr-2"
              >
                <button
                  :disabled="!effectiveHasPrevious"
                  :class="[
                    'p-1.5 rounded-lg transition-colors',
                    effectiveHasPrevious
                      ? 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-solid-4 cursor-pointer'
                      : 'text-n-slate-6 cursor-not-allowed',
                  ]"
                  @click="handleNavigatePrevious"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  :disabled="!effectiveHasNext"
                  :class="[
                    'p-1.5 rounded-lg transition-colors',
                    effectiveHasNext
                      ? 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-solid-4 cursor-pointer'
                      : 'text-n-slate-6 cursor-not-allowed',
                  ]"
                  @click="handleNavigateNext"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              <h1 class="text-2xl font-bold text-n-slate-12">
                {{ ticket?.title || t('TICKETS.LOADING') }}
              </h1>
              <button
                class="p-1 hover:bg-n-solid-4 rounded"
                :title="t('TICKETS.EDIT_TITLE')"
                @click="startEditingTitle"
              >
                <span class="i-lucide-pen w-3 h-3 text-n-slate-11" />
              </button>
            </div>
            <div v-else class="flex gap-2">
              <input
                ref="titleInput"
                v-model="editedTitle"
                type="text"
                class="flex-1 px-3 py-2 text-2xl font-bold border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @keyup.enter="saveTitle"
                @keyup.esc="cancelEditingTitle"
              />
              <Button
                :disabled="loadingTitle || !editedTitle.trim()"
                :is-loading="loadingTitle"
                icon="i-lucide-check"
                size="sm"
                :title="t('TICKETS.BUTTONS.SAVE')"
                class="mt-1"
                @click="saveTitle"
              />
              <Button
                :disabled="loadingTitle"
                icon="i-lucide-x"
                slate
                size="sm"
                :title="t('TICKETS.BUTTONS.CANCEL')"
                class="mt-1"
                @click="cancelEditingTitle"
              />
            </div>
          </div>
          <div>
            <p class="text-n-slate-11 mt-1">
              {{
                t('TICKETS.SLA.TICKET_NUMBER', { number: ticket?.id || '...' })
              }}
              {{ t('TICKETS.BULLET') }} {{ t('TICKETS.CREATED_AT') }}
              {{
                ticket?.created_at
                  ? formatDate(ticket.created_at)
                  : t('TICKETS.LOADING')
              }}
            </p>
          </div>
        </div>

        <!-- Status and Priority -->
        <div
          class="flex items-center space-x-3"
          :class="{ 'mt-auto': isModal, 'mr-6': isModal }"
        >
          <div
            v-if="ticket?.status_name"
            class="inline-flex px-3 py-1 text-sm font-medium rounded-full"
            :style="{
              backgroundColor: ticket.status_colors.background,
              color: ticket.status_colors.text,
            }"
          >
            {{ ticket.status_name }}
          </div>
          <div
            v-if="ticket?.priority_name"
            class="inline-flex px-3 py-1 text-sm font-medium rounded-full"
            :style="{
              backgroundColor: ticket.priority_colors.background,
              color: ticket.priority_colors.text,
            }"
          >
            {{ ticket.priority_name }}
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-n-solid-4 mb-4"
        >
          <svg
            class="h-6 w-6 text-n-slate-10 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        <h3 class="text-sm font-medium text-n-slate-11 mb-2">
          {{ t('TICKETS.LOADING_TICKET') }}
        </h3>
        <p class="text-sm text-n-slate-10">{{ t('TICKETS.WAIT_FOR_DATA') }}</p>
      </div>
    </div>

    <!-- No Ticket State -->
    <div
      v-else-if="!ticket && !loading"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-n-solid-4 mb-4"
        >
          <svg
            class="h-6 w-6 text-n-slate-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 class="text-sm font-medium text-n-slate-11 mb-2">
          {{ t('TICKETS.TICKET_NOT_FOUND') }}
        </h3>
        <p class="text-sm text-n-slate-10">
          {{ t('TICKETS.TICKET_NOT_FOUND_DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-else-if="ticket"
      class="flex-1 flex min-h-0 relative"
      :class="{ 'overflow-y-auto': !isModal, 'overflow-hidden': isModal }"
      :style="isModal ? 'max-height: calc(90vh - 100px);' : ''"
    >
      <!-- Activity History (Center) -->
      <div
        class="flex-1 px-6 w-full mx-auto overflow-y-auto"
        :class="{ 'max-w-3xl': !isModal, 'max-w-full': isModal }"
        :style="isModal ? 'max-height: calc(90vh - 120px);' : ''"
      >
        <!-- Activity Header -->
        <div class="p-6 border-b border-n-weak">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('TICKETS.DESCRIPTION') }}
            </h2>
          </div>
          <div v-if="!isEditingDescription" class="relative pr-4">
            <p class="text-n-slate-12 whitespace-pre-wrap break-words">
              {{ ticket?.description || t('TICKETS.NO_DESCRIPTION') }}
            </p>
            <button
              class="absolute top-0 right-0 p-1 hover:bg-n-solid-4 rounded"
              :title="t('TICKETS.EDIT_DESCRIPTION')"
              @click="startEditingDescription"
            >
              <span class="i-lucide-pen w-3 h-3 text-n-slate-11" />
            </button>
          </div>
          <div v-else class="space-y-2">
            <TextArea
              v-model="editedDescription"
              auto-height
              show-character-count
              :max-length="MAX_DESCRIPTION_LENGTH"
              :placeholder="t('TICKETS.DESCRIPTION_PLACEHOLDER')"
              class="w-full"
            />
            <div class="flex items-center justify-end gap-2">
              <Button
                :disabled="loadingDescription"
                slate
                ghost
                size="sm"
                @click="cancelEditingDescription"
              >
                {{ t('TICKETS.BUTTONS.CANCEL') }}
              </Button>
              <Button
                :disabled="
                  loadingDescription ||
                  !editedDescription.trim() ||
                  editedDescription.length > MAX_DESCRIPTION_LENGTH
                "
                :is-loading="loadingDescription"
                blue
                solid
                size="sm"
                @click="saveDescription"
              >
                {{
                  loadingDescription
                    ? t('TICKETS.SAVING')
                    : t('TICKETS.BUTTONS.SAVE')
                }}
              </Button>
            </div>
          </div>
        </div>
        <!-- Send Email - Only show if conversation exists and is email channel -->
        <div v-if="hasEmailConversation" class="p-6 border-b border-n-weak">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('TICKETS.SEND_EMAIL') }}
            </h2>
            <Button
              v-if="ticket?.contact"
              blue
              solid
              size="sm"
              icon="i-lucide-mail"
              @click="showEmailComposer = true"
            >
              {{ t('TICKETS.SEND_EMAIL_BUTTON') }}
            </Button>
            <div v-else class="text-sm text-n-slate-11">
              {{ t('TICKETS.NO_CONTACT_FOR_EMAIL') }}
            </div>
          </div>
        </div>

        <!-- Add Comment -->
        <AddCommentForm
          v-if="ticket"
          :ticket-id="ticket.id"
          @comment-added="handleCommentAdded"
        />

        <!-- Activities List -->
        <div class="p-6 border-b border-n-weak">
          <h2 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ t('TICKETS.ACTIVITY_HISTORY') }}
          </h2>
          <div class="flex-1">
            <div v-if="loading" class="flex items-center justify-center h-32">
              <div class="text-n-slate-11">{{ t('TICKETS.LOADING') }}</div>
            </div>

            <div
              v-else-if="loadingHistory"
              class="flex items-center justify-center h-32"
            >
              <div class="flex items-center space-x-2 text-n-slate-11">
                <svg
                  class="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{{ t('TICKETS.UPDATING_ACTIVITIES') }}</span>
              </div>
            </div>

            <div
              v-else-if="activities.length === 0"
              class="flex items-center justify-center h-32"
            >
              <div class="text-n-slate-11">
                {{ t('TICKETS.NO_ACTIVITIES_FOUND') }}
              </div>
            </div>

            <div v-else-if="activities.length > 0" class="space-y-6">
              <div
                v-for="activity in activities"
                :key="activity.id"
                class="flex space-x-4"
              >
                <!-- Avatar (hidden for system/email actions) -->
                <div v-if="!activity.systemAction" class="flex-shrink-0">
                  <div
                    class="h-10 w-10 rounded-full bg-n-solid-1 flex items-center justify-center"
                  >
                    <span class="text-sm font-medium text-n-slate-11">
                      {{ activity.avatar }}
                    </span>
                  </div>
                </div>

                <!-- Activity Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center space-x-2 mb-1">
                    <template v-if="activity.systemAction">
                      <span class="text-sm text-n-slate-11">
                        {{ activity.newValue || activity.action }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ activity.user }}
                      </span>
                      <span class="text-sm text-n-slate-11">
                        {{ activity.action }}
                      </span>
                    </template>
                    <TicketActivityContentText
                      :activity="activity"
                      :account-id="route.params.accountId"
                      :conversation-display-id="ticket?.conversation_display_id"
                    />
                  </div>
                  <!-- Show SLA changes using structured data from backend -->
                  <div
                    v-if="
                      activity.action === 'SLA atualizado' &&
                      activity.slaChanges &&
                      activity.slaChanges.length > 0
                    "
                    class="text-sm mb-2 flex items-center gap-2 flex-wrap"
                  >
                    <span
                      v-for="(change, index) in formatSlaChange(
                        activity.slaChanges
                      )"
                      :key="index"
                      v-html="change"
                    />
                  </div>
                  <!-- Show old and new values for other actions when both exist and are different -->
                  <div
                    v-else-if="
                      activity.oldValue &&
                      activity.newValue &&
                      activity.oldValue !== activity.newValue &&
                      activity.action !== 'Comentou' &&
                      activity.action !== 'SLA atualizado'
                    "
                    class="text-sm mb-2"
                  >
                    <div class="flex flex-row gap-1">
                      <span class="text-n-slate-11 line-through">{{
                        formatHistoryValue(activity.oldValue)
                      }}</span>
                      <span class="text-n-slate-10 mx-1">{{
                        t('TICKETS.ARROW')
                      }}</span>
                      <span class="text-n-slate-12 font-medium">{{
                        formatHistoryValue(activity.newValue)
                      }}</span>
                    </div>
                  </div>
                  <div
                    v-if="activity.content && activity.action === 'Comentou'"
                    class="text-sm text-n-slate-11 bg-n-solid-3 rounded-lg p-3 mb-2 max-w-full overflow-y-auto overflow-x-hidden"
                  >
                    {{ activity.content }}
                  </div>
                  <TicketAttachments :attachments="activity.attachments" />
                  <div
                    class="flex items-center space-x-2 text-xs text-n-slate-10"
                  >
                    <span>{{ formatDate(activity.timestamp) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel - Ticket Data -->
      <div
        v-if="ticket"
        class="overflow-y-auto"
        :class="{ 'w-80': !isModal, 'w-72': isModal }"
        :style="isModal ? 'max-height: calc(90vh - 100px);' : ''"
      >
        <div class="p-6 space-y-6 bg-n-solid-1">
          <div class="flex items-center justify-center">
            <Button
              class="px-8"
              :is-loading="loadingResolve"
              @click="toggleResolveStatus"
            >
              {{
                ticket.status_name === 'Resolvido'
                  ? t('TICKETS.REOPEN_TICKET')
                  : t('TICKETS.MARK_AS_RESOLVED')
              }}
            </Button>
          </div>

          <!-- PRIORITY ASSIGNEE TEAM CONTACT -->
          <div>
            <div class="space-y-3">
              {{ t('TICKETS.PRIORITY') }}
              <PrioritySelector
                :priorities="priorities"
                :selected-priority-id="ticket.priority_id"
                :update-history
                :ticket-id="ticket.id"
                :disabled="loadingPriority"
              />
            </div>
            <div class="space-y-3">
              {{ t('TICKETS.ASSIGNEE') }}
              <AssigneeSelector
                :agents="agents"
                :selected-assignee-id="ticket.assignee_id"
                :update-history
                :ticket-id="ticket.id"
                :disabled="loadingAssignee"
              />
            </div>

            <div class="space-y-3">
              {{ t('TICKETS.TEAM') }}
              <TeamSelector
                :teams="teamsForSelector"
                :selected-team-id="ticket.team_id"
                :update-history
                :ticket-id="ticket.id"
                :disabled="loadingTeam || hasServices"
              />
            </div>

            <!-- Contact -->
            <div class="space-y-3">
              {{ t('TICKETS.CONTACT') }}
              <ContactSelector
                :contacts="contacts"
                :selected-contact-id="ticket.contact_id"
                :initial-contact="ticket.contact"
                :update-history
                :ticket-id="ticket.id"
                :disabled="loadingContact"
              />
            </div>

            <!-- Business Account -->
            <div class="space-y-3">
              {{ t('TICKETS.BUSINESS_ACCOUNT') }}
              <BusinessAccountSelector
                :business-accounts="businessAccounts"
                :selected-business-account-id="ticket.business_account_id"
                :update-history
                :ticket-id="ticket.id"
              />
            </div>

            <!-- Contact Information -->
            <div v-if="ticket.contact">
              <h3
                class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
              >
                {{ t('TICKETS.CONTACT_INFORMATION') }}
              </h3>
              <div class="space-y-2">
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('TICKETS.NAME') }}{{ t('TICKETS.COLON') }}
                  </span>
                  <span class="text-sm text-n-slate-11 ml-2">{{
                    ticket.contact.name
                  }}</span>
                </div>
                <div v-if="ticket.contact.email">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('TICKETS.EMAIL') }}{{ t('TICKETS.COLON') }}
                  </span>
                  <span class="text-sm text-n-slate-11 ml-2">{{
                    ticket.contact.email
                  }}</span>
                </div>
                <div v-if="ticket.contact.phone">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('TICKETS.PHONE') }}{{ t('TICKETS.COLON') }}
                  </span>
                  <span class="text-sm text-n-slate-11 ml-2">{{
                    ticket.contact.phone
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Service -->
          <div v-if="hasServices">
            <h3
              class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
            >
              {{ t('TICKETS.SERVICE.LABEL') }}
            </h3>
            <div class="space-y-3">
              <div class="space-y-1">
                <label class="text-sm text-n-slate-11">{{
                  t('TICKETS.SERVICE.LABEL')
                }}</label>
                <ServiceSelector
                  :services="services || []"
                  :selected-service-id="serviceForm.service"
                  :disabled="updatingService"
                  @update:selected-service-id="serviceForm.service = $event"
                />
              </div>
              <div
                v-if="subServices.length > 0 && serviceForm.service"
                class="space-y-1"
              >
                <label class="text-sm text-n-slate-11">{{
                  t('TICKETS.SUB_SERVICE.LABEL')
                }}</label>
                <SubServiceSelector
                  :sub-services="subServices"
                  :selected-sub-service-id="serviceForm.subService"
                  :disabled="updatingService"
                  @update:selected-sub-service-id="
                    serviceForm.subService = $event
                  "
                />
              </div>
              <div v-if="updatingService" class="flex items-center gap-2">
                <div
                  class="flex items-center space-x-2 text-sm text-n-slate-11"
                >
                  <svg
                    class="h-4 w-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>{{ t('TICKETS.SAVING') }}</span>
                </div>
              </div>
              <p v-if="serviceError" class="text-red-500 text-sm mt-1">
                {{ serviceError }}
              </p>
            </div>
          </div>

          <!-- Board and Column -->
          <div>
            <h3
              class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
            >
              {{ t('TICKETS.BOARD') }}
            </h3>
            <div class="space-y-3">
              {{ t('TICKETS.BOARD') }}
              <BoardSelector
                :boards="boards"
                :selected-board-id="ticket.board_id"
                :update-history
                :ticket-id="ticket.id"
                disabled
              />
            </div>
            <div class="space-y-3 mt-3">
              {{ t('TICKETS.COLUMN') }}
              <BoardColumnSelector
                :columns="boardColumns"
                :selected-column-id="ticket.board_column_id"
                :board-id="ticket.board_id"
                :update-history
                :ticket-id="ticket.id"
              />
            </div>
          </div>

          <!-- SLA Policy -->
          <div>
            <h3
              class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
            >
              {{ t('TICKETS.SLA_POLICY') }}
            </h3>
            <div class="space-y-3">
              {{ t('TICKETS.SLA_LABEL') }}
              <SlaPolicySelector
                :sla-policies="slaPolicies"
                :selected-sla-policy-id="ticket.sla_policy_id"
                :ticket-id="ticket.id"
                :update-history
                :disabled="hasServices"
                :sla-events="ticket.sla_events || []"
                :ticket-created-at="ticket.created_at"
                :sla-deadlines="ticket.sla_deadlines || {}"
              />
            </div>
          </div>

          <!-- SLA Status & Events -->
          <div v-if="ticket.applied_sla">
            <h3
              class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
            >
              {{ t('TICKETS.SLA_STATUS') }}
            </h3>
            <div class="space-y-4">
              <!-- SLA Status -->
              <div class="bg-n-solid-2 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('TICKETS.SLA.CURRENT_STATUS')
                    }}{{ t('TICKETS.COLON') }}
                  </span>
                  <span
                    class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                    :class="getSlaStatusClass(ticket.applied_sla.sla_status)"
                  >
                    {{ getSlaStatusText(ticket.applied_sla.sla_status) }}
                  </span>
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ t('TICKETS.SLA.APPLIED_AT') }}{{ t('TICKETS.COLON') }}
                  {{ formatDate(ticket.applied_sla.created_at) }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ t('TICKETS.SLA.LAST_UPDATE') }}{{ t('TICKETS.COLON') }}
                  {{ formatDate(ticket.applied_sla.updated_at) }}
                </div>
              </div>

              <!-- SLA Events -->
              <div v-if="ticket.sla_events && ticket.sla_events.length > 0">
                <h4 class="text-sm font-medium text-n-slate-12 mb-2">
                  {{ t('TICKETS.SLA.SLA_EVENTS') }}
                </h4>
                <div class="space-y-3">
                  <UpdatedSlaEventComponent
                    v-for="event in ticket.sla_events.filter(
                      e => e.event_type === 'updated'
                    )"
                    :key="`updated-${event.id}`"
                    :event="event"
                    compact
                  />
                  <DeletedSlaEventComponent
                    v-for="event in ticket.sla_events.filter(
                      e => e.event_type === 'deleted'
                    )"
                    :key="`deleted-${event.id}`"
                    :event="event"
                    compact
                  />
                  <SlaComponent
                    v-for="event in ticket.sla_events.filter(
                      e =>
                        e.event_type !== 'updated' && e.event_type !== 'deleted'
                    )"
                    :key="event.id"
                    :event="event"
                    show-details
                    :show-icon="showIcon"
                    compact
                  />
                </div>
              </div>

              <!-- No SLA Events -->
              <div v-else class="p-3 bg-n-solid-3 rounded-lg text-center">
                <div class="text-sm text-n-slate-11">
                  {{ t('TICKETS.SLA.NO_SLA_EVENTS') }}
                </div>
              </div>
            </div>
          </div>

          <!-- No Applied SLA -->
          <div v-else-if="ticket.sla_policy_id">
            <h3
              class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
            >
              {{ t('TICKETS.SLA_STATUS') }}
            </h3>
            <div class="p-4 bg-n-solid-2 rounded-lg text-center">
              <div class="text-sm text-n-slate-11">
                {{ t('TICKETS.SLA.NOT_APPLIED_YET') }}
              </div>
              <div class="text-xs text-n-slate-10 mt-1">
                {{ t('TICKETS.SLA.WILL_BE_APPLIED_AUTOMATICALLY') }}
              </div>
            </div>
          </div>

          <!-- Custom Attributes -->
          <div>
            <TicketCustomAttributesDisplay
              :ticket="ticket"
              :editable
              :update-history
            />
          </div>

          <!-- Timestamps -->
          <div v-if="ticket">
            <h3
              class="text-sm font-medium text-n-slate-11 uppercase tracking-wider mb-3"
            >
              {{ t('TICKETS.SYSTEM_INFORMATION') }}
            </h3>
            <div class="space-y-2 text-sm">
              <div>
                <span class="text-n-slate-11">
                  {{ t('TICKETS.CREATED') }}{{ t('TICKETS.COLON') }}
                </span>
                <span class="text-n-slate-12 ml-2">{{
                  formatDate(ticket.created_at)
                }}</span>
              </div>
              <div>
                <span class="text-n-slate-11">
                  {{ t('TICKETS.UPDATED') }}{{ t('TICKETS.COLON') }}
                </span>
                <span class="text-n-slate-12 ml-2">{{
                  formatDate(ticket.updated_at)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="pt-4 border-t border-n-weak">
            <div
              class="space-y-2 flex flex-col gap-2 justify-center items-center"
            >
              <Button
                class="px-8"
                ghost
                color="slate"
                :is-loading="loadingCancel"
                @click="cancelTicket"
              >
                {{ t('TICKETS.CANCEL_TICKET') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Composer Modal -->
    <TicketEmailComposer
      v-if="ticket"
      :ticket="ticket"
      :show="showEmailComposer"
      @close="showEmailComposer = false"
      @email-sent="handleEmailSent"
    />
  </div>
</template>
